import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getSubscription() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const reg = await navigator.serviceWorker.ready;
  return await reg.pushManager.getSubscription();
}

export default function PushNotifications() {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getSubscription().then((s) => setSubscribed(!!s));
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Izin notifikasi ditolak oleh browser");
        return;
      }
      const { data } = await api.get("/push/public-key");
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notifikasi diaktifkan");
    } catch (e) {
      toast.error(e.message || "Gagal mengaktifkan notifikasi");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const sub = await getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
      }
      setSubscribed(false);
      toast.success("Notifikasi dimatikan");
    } catch (e) {
      toast.error(e.message || "Gagal mematikan notifikasi");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) toast.success(`Uji notifikasi terkirim (${data.sent})`);
      else toast.error("Tidak ada langganan aktif. Aktifkan notifikasi dulu.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Gagal mengirim uji");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section className="bg-white border border-[#E5E7EB] rounded-md p-6" data-testid="push-notifications-section">
      <div className="flex items-center gap-2 mb-6">
        <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[#0055FF]/10 text-[#0055FF]">
          <Bell className="h-4 w-4" />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight">Notifikasi Push</h2>
          <p className="text-xs text-[#9CA3AF]">
            Terima alarm tiket baru langsung di HP walaupun aplikasi tertutup.
          </p>
        </div>
      </div>

      {!supported ? (
        <div className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-md px-3 py-2">
          Browser Anda belum mendukung push notification. Buka aplikasi ini di Chrome Android atau desktop terbaru.
        </div>
      ) : permission === "denied" ? (
        <div className="text-sm text-[#4B5563]">
          Izin notifikasi telah <b>diblokir</b> untuk situs ini. Aktifkan lewat pengaturan browser (ikon gembok di address bar) lalu muat ulang halaman.
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full"
            style={{
              background: subscribed ? "#D1FAE5" : "#FEF3C7",
              color: subscribed ? "#065F46" : "#92400E",
            }}
            data-testid="push-status"
          >
            {subscribed ? "AKTIF DI PERANGKAT INI" : "BELUM DIAKTIFKAN"}
          </div>

          {subscribed ? (
            <>
              <button
                type="button"
                data-testid="btn-push-disable"
                disabled={busy}
                onClick={disable}
                className="h-9 px-4 rounded-md border border-[#E5E7EB] text-[#4B5563] text-sm font-semibold hover:bg-[#F8F9FA] flex items-center gap-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
                Matikan Notifikasi
              </button>
              <button
                type="button"
                data-testid="btn-push-test"
                disabled={testing}
                onClick={sendTest}
                className="h-9 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold flex items-center gap-2"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Kirim Uji Notifikasi
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="btn-push-enable"
              disabled={busy}
              onClick={enable}
              className="h-9 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold flex items-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              Aktifkan Notifikasi Tiket
            </button>
          )}
        </div>
      )}
    </section>
  );
}
